import { ReactNode, useState, useEffect } from 'react';
import { Portal } from '@headlessui/react';
import { Button, Icon } from '@iwoca/orion';
import clsx from 'clsx';
import { getEnvironment } from 'utils/environmentHelper/environmentHelper';
import styles from './Drawer.module.css';

type TProps = {
  show: boolean;
  title?: string;
  onClose: () => void;
  children: ReactNode;
};

export const Drawer = ({ show, onClose, title, children }: TProps) => {
  const [isShowing, setisShowing] = useState(show);

  useEffect(() => {
    if (show) {
      setisShowing(true);
    } else {
      const timeoutId = setTimeout(() => {
        setisShowing(false);
      }, 500);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [show]);

  if (!isShowing) return null;
  return (
    <Portal>
      <div
        className={clsx(styles.container, {
          [styles.show]: show,
          [styles.hide]: !show,
          [styles.isStaging]: getEnvironment() === 'staging',
        })}
        role="dialog"
        data-testid="drawer"
      >
        <Button
          iconOnly
          className={styles.closeButton}
          variant="tertiary"
          size="small"
          onClick={onClose}
          data-testid="close-button"
        >
          <Icon icon="timesDefault" className={styles.closeIcon} />
        </Button>

        {title && <h3 className={styles.title}>{title}</h3>}
        {children}
      </div>
      {show && (
        <div
          className={styles.backdrop}
          onClick={onClose}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' ? onClose() : undefined)}
        />
      )}
    </Portal>
  );
};
